import type {
  RaspadinhaContemplatedPrizeImage,
  RaspadinhaDayWinningSchedule,
  RaspadinhaTimeSlotWinner,
  RaspadinhaWinningTimeSlot,
} from '@/types/raspadinha-campaign'
import { getContemplatedPrizeTitle } from './contemplated-prize-images'

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/
const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

function cloneWinner(winner: RaspadinhaTimeSlotWinner): RaspadinhaTimeSlotWinner {
  return { ...winner }
}

function normalizeTimeSlot(slot: RaspadinhaWinningTimeSlot): RaspadinhaWinningTimeSlot {
  return {
    id: slot.id,
    time: normalizeWinningTime(slot.time),
    is_active: slot.is_active ?? true,
    contemplated_prize_id: slot.contemplated_prize_id ?? '',
    winners: (slot.winners ?? []).map(cloneWinner),
  }
}

function mapTimeSlot(
  schedules: RaspadinhaDayWinningSchedule[],
  date: string,
  slotId: string,
  updater: (slot: RaspadinhaWinningTimeSlot) => RaspadinhaWinningTimeSlot
): RaspadinhaDayWinningSchedule[] {
  return schedules.map((schedule) =>
    schedule.date === date
      ? {
          ...schedule,
          time_slots: schedule.time_slots.map((slot) =>
            slot.id === slotId ? updater(slot) : slot
          ),
        }
      : schedule
  )
}

export function toLocalDateKey(isoDateTime: string): string {
  const date = new Date(isoDateTime)
  if (Number.isNaN(date.getTime())) return ''

  return formatLocalDateKey(date)
}

function formatLocalDateKey(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export function listCampaignCalendarDates(
  startsAt: string | null,
  endsAt: string | null
): string[] {
  if (!startsAt || !endsAt) return []

  const startKey = toLocalDateKey(startsAt)
  const endKey = toLocalDateKey(endsAt)

  if (!startKey || !endKey || startKey > endKey) return []

  const dates: string[] = []
  const cursor = new Date(`${startKey}T12:00:00`)
  const endCursor = new Date(`${endKey}T12:00:00`)

  while (cursor.getTime() <= endCursor.getTime()) {
    dates.push(formatLocalDateKey(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }

  return dates
}

export type CampaignDateStatus = 'past' | 'today' | 'future'

export function getCampaignDateStatus(date: string, referenceDate: Date = new Date()): CampaignDateStatus {
  if (!DATE_KEY_PATTERN.test(date)) return 'future'

  const todayKey = formatLocalDateKey(referenceDate)

  if (date < todayKey) return 'past'
  if (date === todayKey) return 'today'
  return 'future'
}

export function formatCampaignDateLabel(date: string, dayNumber?: number): string {
  if (!DATE_KEY_PATTERN.test(date)) return date

  const parsed = new Date(`${date}T12:00:00`)
  if (Number.isNaN(parsed.getTime())) return date

  const formattedDate = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed)

  const weekday = capitalizeWeekdayLabel(
    new Intl.DateTimeFormat('pt-BR', { weekday: 'long' }).format(parsed)
  )

  const dateWithWeekday = `${formattedDate} (${weekday})`

  if (dayNumber === undefined || !Number.isFinite(dayNumber) || dayNumber < 1) {
    return dateWithWeekday
  }

  const dayPrefix = `DIA ${String(Math.trunc(dayNumber)).padStart(2, '0')}`
  return `${dayPrefix} - ${dateWithWeekday}`
}

function capitalizeWeekdayLabel(weekday: string): string {
  return weekday
    .split('-')
    .map((part) => part.charAt(0).toLocaleUpperCase('pt-BR') + part.slice(1))
    .join('-')
}

export function buildCampaignDayNumberByDate(
  schedules: readonly Pick<RaspadinhaDayWinningSchedule, 'date'>[]
): Map<string, number> {
  const sortedDates = [...new Set(schedules.map((schedule) => schedule.date))].sort()

  return new Map(sortedDates.map((date, index) => [date, index + 1]))
}

export function createEmptyDateWinningSchedules(
  dates: readonly string[]
): RaspadinhaDayWinningSchedule[] {
  return dates.map((date) => ({
    date,
    time_slots: [],
  }))
}

export function ensureDateWinningSchedules(
  schedules: RaspadinhaDayWinningSchedule[] | undefined,
  startsAt: string | null,
  endsAt: string | null
): RaspadinhaDayWinningSchedule[] {
  const dates = listCampaignCalendarDates(startsAt, endsAt)

  if (dates.length === 0) {
    return []
  }

  const existingByDate = new Map(
    (schedules ?? []).map((schedule) => [
      schedule.date,
      {
        date: schedule.date,
        time_slots: schedule.time_slots.map(normalizeTimeSlot),
      },
    ])
  )

  return dates.map((date) => existingByDate.get(date) ?? { date, time_slots: [] })
}

export function createWinningTimeSlot(): RaspadinhaWinningTimeSlot {
  return {
    id: `win-time-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    time: '',
    is_active: true,
    contemplated_prize_id: '',
    winners: [],
  }
}

export function createTimeSlotWinner(): RaspadinhaTimeSlotWinner {
  return {
    id: `win-winner-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: '',
    document: '',
    prize_label: '',
    notes: '',
  }
}

export function normalizeWinningTime(value: string): string {
  return value.trim()
}

export function isValidWinningTime(value: string): boolean {
  return TIME_PATTERN.test(normalizeWinningTime(value))
}

export const WINNING_TIME_SLOT_PRIZE_REQUIRED_MESSAGE =
  'Selecione o prêmio em todos os horários de contemplação.'

export const WINNING_TIME_WITHOUT_PRIZE_MESSAGE =
  'Selecione o prêmio antes de definir o horário.'

export const ADD_TIME_SLOT_REQUIRES_PRIZE_CATALOG_MESSAGE =
  'Cadastre ao menos um prêmio contemplado antes de adicionar horários.'

export function hasWinningTimeSlotPrize(slot: RaspadinhaWinningTimeSlot): boolean {
  return slot.contemplated_prize_id.trim().length > 0
}

export function canSetWinningTimeSlotTime(
  slot: RaspadinhaWinningTimeSlot,
  time: string
): boolean {
  const normalizedTime = normalizeWinningTime(time)
  if (normalizedTime === '') {
    return true
  }

  return hasWinningTimeSlotPrize(slot)
}

export function canClearWinningTimeSlotPrize(slot: RaspadinhaWinningTimeSlot): boolean {
  return !isValidWinningTime(slot.time)
}

export function validateWinningDateSchedules(
  schedules: RaspadinhaDayWinningSchedule[],
  contemplatedPrizes: RaspadinhaContemplatedPrizeImage[] = []
): string | null {
  const validPrizeIds = new Set(contemplatedPrizes.map((prize) => prize.id))

  for (const schedule of schedules) {
    for (const slot of schedule.time_slots) {
      if (!hasWinningTimeSlotPrize(slot)) {
        const dayLabel = formatCampaignDateLabel(schedule.date)
        return `${WINNING_TIME_SLOT_PRIZE_REQUIRED_MESSAGE} (${dayLabel})`
      }

      const prizeId = slot.contemplated_prize_id.trim()
      if (prizeId !== '' && !validPrizeIds.has(prizeId)) {
        const dayLabel = formatCampaignDateLabel(schedule.date)
        return `O prêmio selecionado em ${dayLabel} não existe mais. Escolha outro prêmio.`
      }
    }
  }

  return null
}

export function formatWinningTimeLabel(time: string): string {
  return isValidWinningTime(time) ? time : 'Horário não definido'
}

export function addWinningTimeSlot(
  schedules: RaspadinhaDayWinningSchedule[],
  date: string,
  contemplatedPrizeId = ''
): RaspadinhaDayWinningSchedule[] {
  const newSlot = createWinningTimeSlot()
  newSlot.contemplated_prize_id = contemplatedPrizeId.trim()

  return schedules.map((schedule) =>
    schedule.date === date
      ? { ...schedule, time_slots: [...schedule.time_slots, newSlot] }
      : schedule
  )
}

export type GeneratedWinningTimeSlotInput = {
  time: string
  contemplated_prize_id: string
}

export function addGeneratedWinningTimeSlots(
  schedules: RaspadinhaDayWinningSchedule[],
  date: string,
  slots: readonly GeneratedWinningTimeSlotInput[]
): RaspadinhaDayWinningSchedule[] {
  const newSlots = slots.map((slot) => ({
    ...createWinningTimeSlot(),
    time: normalizeWinningTime(slot.time),
    contemplated_prize_id: slot.contemplated_prize_id.trim(),
  }))

  return schedules.map((schedule) =>
    schedule.date === date
      ? { ...schedule, time_slots: [...schedule.time_slots, ...newSlots] }
      : schedule
  )
}

export const REMOVE_TIME_SLOT_WITH_WINNERS_MESSAGE =
  'Não é possível excluir um horário que já possui ganhadores cadastrados. Desative o horário em vez disso.'

export function canRemoveWinningTimeSlot(slot: RaspadinhaWinningTimeSlot): boolean {
  return slot.winners.length === 0
}

export function removeWinningTimeSlot(
  schedules: RaspadinhaDayWinningSchedule[],
  date: string,
  slotId: string
): RaspadinhaDayWinningSchedule[] {
  const schedule = schedules.find((entry) => entry.date === date)
  const slot = schedule?.time_slots.find((entry) => entry.id === slotId)

  if (slot && !canRemoveWinningTimeSlot(slot)) {
    throw new Error(REMOVE_TIME_SLOT_WITH_WINNERS_MESSAGE)
  }

  return schedules.map((entry) =>
    entry.date === date
      ? {
          ...entry,
          time_slots: entry.time_slots.filter((timeSlot) => timeSlot.id !== slotId),
        }
      : entry
  )
}

export function updateWinningTimeSlot(
  schedules: RaspadinhaDayWinningSchedule[],
  date: string,
  slotId: string,
  time: string
): RaspadinhaDayWinningSchedule[] {
  return mapTimeSlot(schedules, date, slotId, (slot) => ({
    ...slot,
    time: normalizeWinningTime(time),
  }))
}

export function updateWinningTimeSlotActive(
  schedules: RaspadinhaDayWinningSchedule[],
  date: string,
  slotId: string,
  isActive: boolean
): RaspadinhaDayWinningSchedule[] {
  return mapTimeSlot(schedules, date, slotId, (slot) => ({
    ...slot,
    is_active: isActive,
  }))
}

export function updateWinningTimeSlotPrize(
  schedules: RaspadinhaDayWinningSchedule[],
  date: string,
  slotId: string,
  contemplatedPrizeId: string
): RaspadinhaDayWinningSchedule[] {
  return mapTimeSlot(schedules, date, slotId, (slot) => ({
    ...slot,
    contemplated_prize_id: contemplatedPrizeId.trim(),
  }))
}

export function addWinnerToTimeSlot(
  schedules: RaspadinhaDayWinningSchedule[],
  date: string,
  slotId: string
): RaspadinhaDayWinningSchedule[] {
  return mapTimeSlot(schedules, date, slotId, (slot) => ({
    ...slot,
    winners: [...slot.winners, createTimeSlotWinner()],
  }))
}

export function removeWinnerFromTimeSlot(
  schedules: RaspadinhaDayWinningSchedule[],
  date: string,
  slotId: string,
  winnerId: string
): RaspadinhaDayWinningSchedule[] {
  return mapTimeSlot(schedules, date, slotId, (slot) => ({
    ...slot,
    winners: slot.winners.filter((winner) => winner.id !== winnerId),
  }))
}

export function updateWinnerInTimeSlot(
  schedules: RaspadinhaDayWinningSchedule[],
  date: string,
  slotId: string,
  winnerId: string,
  patch: Partial<Omit<RaspadinhaTimeSlotWinner, 'id'>>
): RaspadinhaDayWinningSchedule[] {
  return mapTimeSlot(schedules, date, slotId, (slot) => ({
    ...slot,
    winners: slot.winners.map((winner) =>
      winner.id === winnerId ? { ...winner, ...patch } : winner
    ),
  }))
}

export function countConfiguredWinningTimeSlots(
  schedules: RaspadinhaDayWinningSchedule[]
): number {
  return schedules.reduce(
    (total, schedule) =>
      total +
      schedule.time_slots.filter((slot) => slot.is_active && isValidWinningTime(slot.time)).length,
    0
  )
}

export function countDatesWithWinningTimes(schedules: RaspadinhaDayWinningSchedule[]): number {
  return schedules.filter((schedule) =>
    schedule.time_slots.some((slot) => isValidWinningTime(slot.time))
  ).length
}

export function countTotalWinnersInSchedules(
  schedules: RaspadinhaDayWinningSchedule[]
): number {
  return schedules.reduce(
    (total, schedule) =>
      total +
      schedule.time_slots.reduce((slotTotal, slot) => slotTotal + slot.winners.length, 0),
    0
  )
}

export function countWinnersInDaySchedule(schedule: RaspadinhaDayWinningSchedule): number {
  return schedule.time_slots.reduce((total, slot) => total + slot.winners.length, 0)
}

export function matchesTimeSlotSearch(
  slot: RaspadinhaWinningTimeSlot,
  query: string,
  contemplatedPrizes: RaspadinhaContemplatedPrizeImage[] = []
): boolean {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return true

  if (slot.time.toLowerCase().includes(normalizedQuery)) {
    return true
  }

  const slotPrizeTitle = getContemplatedPrizeTitle(contemplatedPrizes, slot.contemplated_prize_id)
  if (slotPrizeTitle.toLowerCase().includes(normalizedQuery)) {
    return true
  }

  return slot.winners.some((winner) => {
    const haystack = [winner.name, winner.document, winner.prize_label, winner.notes]
      .join(' ')
      .toLowerCase()
    return haystack.includes(normalizedQuery)
  })
}
